import {
  PERM_ADMIN,
  PERM_NONE,
  PERM_READ,
  PERM_WRITE,
} from '../constants';

export type PermissionCheckMode = 'any' | 'all';

export const checkPermissionActions = (
  availableActions: string[] | undefined,
  requestedActions: string | string[],
  mode: PermissionCheckMode = 'any',
): boolean => {
  const safeAvailableActions = (availableActions || []).filter(Boolean);
  const safeRequestedActions = (Array.isArray(requestedActions) ? requestedActions : [requestedActions])
    .filter(Boolean);

  if (!safeRequestedActions.length) return true;
  if (!safeAvailableActions.length) return false;

  if (mode === 'all') {
    return safeRequestedActions.every((action) => safeAvailableActions.includes(action));
  }

  return safeRequestedActions.some((action) => safeAvailableActions.includes(action));
};

export const roleToPermissionSet = (
  role: string | null | undefined,
  internalRole?: string | null | undefined,
) => {
  const normalized = String(role || '').toLowerCase();
  const normalizedInternalRole = String(internalRole || '').toLowerCase();

  if (normalized === 'admin' || normalizedInternalRole === 'internal admin') {
    return {
      products: PERM_ADMIN,
      packages: PERM_ADMIN,
      printRuns: PERM_ADMIN,
      templates: PERM_ADMIN,
      landingPages: PERM_ADMIN,
      brands: PERM_ADMIN,
      facilities: PERM_ADMIN,
      imports: PERM_ADMIN,
      userManagement: PERM_ADMIN,
      organizationManagement: PERM_ADMIN,
    };
  }

  if (normalizedInternalRole === 'internal standard') {
    return {
      products: PERM_WRITE,
      packages: PERM_WRITE,
      printRuns: PERM_WRITE,
      templates: PERM_WRITE,
      landingPages: PERM_WRITE,
      brands: PERM_WRITE,
      facilities: PERM_WRITE,
      imports: PERM_WRITE,
      userManagement: PERM_WRITE,
      organizationManagement: PERM_READ,
    };
  }

  if (normalized === 'manager') {
    return {
      products: PERM_WRITE,
      packages: PERM_WRITE,
      printRuns: PERM_WRITE,
      templates: PERM_WRITE,
      landingPages: PERM_WRITE,
      brands: PERM_WRITE,
      facilities: PERM_READ,
      imports: PERM_WRITE,
      userManagement: PERM_READ,
      organizationManagement: PERM_READ,
    };
  }

  if (normalized === 'designer') {
    return {
      products: PERM_WRITE,
      packages: PERM_READ,
      printRuns: PERM_READ,
      templates: PERM_WRITE,
      landingPages: PERM_NONE,
      brands: PERM_WRITE,
      facilities: PERM_READ,
      imports: PERM_NONE,
      userManagement: PERM_NONE,
      organizationManagement: PERM_READ,
    };
  }

  if (normalizedInternalRole === 'internal viewer') {
    return {
      products: PERM_READ,
      packages: PERM_READ,
      printRuns: PERM_READ,
      templates: PERM_READ,
      landingPages: PERM_READ,
      brands: PERM_READ,
      facilities: PERM_READ,
      imports: PERM_READ,
      userManagement: PERM_READ,
      organizationManagement: PERM_READ,
    };
  }

  return {
    products: PERM_READ,
    packages: PERM_READ,
    printRuns: PERM_READ,
    templates: PERM_READ,
    landingPages: PERM_READ,
    brands: PERM_READ,
    facilities: PERM_READ,
    imports: PERM_NONE,
    userManagement: PERM_NONE,
    organizationManagement: PERM_NONE,
  };
};

const permissionActionNames: Record<string, string> = {
  products: 'Products',
  packages: 'Packages',
  printRuns: 'PrintRuns',
  templates: 'Templates',
  landingPages: 'CustomLandingPages',
  brands: 'Brands',
  facilities: 'Facilities',
  imports: 'Imports',
  userManagement: 'UserManagement',
  organizationManagement: 'OrganizationManagement',
};

export const roleToActionList = (
  role: string | null | undefined,
  internalRole?: string | null | undefined,
) => {
  const permissionSet = roleToPermissionSet(role, internalRole);
  const actions = new Set<string>();

  Object.entries(permissionSet).forEach(([key, value]) => {
    const actionName = permissionActionNames[key];
    if (!actionName || Number(value) <= PERM_NONE) return;

    actions.add(`Read${actionName}`);
    if (Number(value) >= PERM_WRITE) actions.add(`Write${actionName}`);
    if (Number(value) >= PERM_ADMIN) actions.add(`Admin${actionName}`);
  });

  return Array.from(actions);
};

export const normalizePermissionActions = (actions: unknown): string[] => {
  if (!Array.isArray(actions)) return [];

  return Array.from(new Set(
    actions.filter((action): action is string => typeof action === 'string' && Boolean(action)),
  ));
};

export const resolveOrganizationRoleActions = (
  role: string | null | undefined,
  tokenActions?: unknown,
): string[] => {
  const normalizedTokenActions = normalizePermissionActions(tokenActions);
  return normalizedTokenActions.length ? normalizedTokenActions : roleToActionList(role);
};

type ResolveRoleActionsInput = {
  role: string | null | undefined;
  organizationActions?: unknown;
  internalRole?: string | null | undefined;
};

// Preserve the role precedence used before the refactor. Regular actions for
// internal users are resolved here; internal-only actions stay in permissionsInternal.
export const resolveRoleActions = ({
  role,
  organizationActions,
  internalRole,
}: ResolveRoleActionsInput): string[] => {
  const normalizedInternalRole = String(internalRole || '').trim().toLowerCase();
  const hasKnownInternalRole = [
    'internal admin',
    'internal standard',
    'internal viewer',
  ].includes(normalizedInternalRole);

  if (hasKnownInternalRole) {
    return roleToActionList(role, internalRole);
  }

  return resolveOrganizationRoleActions(role, organizationActions);
};

export const normalizeScopeIds = (value) =>
  (Array.isArray(value) ? value : [value])
    .map((id) => Number(id))
    .filter((id, index, arr) => Number.isFinite(id) && id > 0 && arr.indexOf(id) === index);

export const validateUserAccessScope = (values) => {
  const allMarkets = values?.allMarkets === true;
  const allFacilities = values?.allFacilities === true;
  const allBrands = values?.allBrands === true;

  const marketIds = normalizeScopeIds(values?.marketIds);
  const defaultMarketId = normalizeScopeIds(values?.defaultMarketId)[0] ?? null;
  const facilityIds = normalizeScopeIds(values?.facilityIds);
  const brandIds = normalizeScopeIds(values?.brandIds);

  if (!allMarkets && marketIds.length === 0) {
    return 'Select at least one market or set Market Access to All.';
  }

  if (!allMarkets && defaultMarketId !== null && !marketIds.includes(defaultMarketId)) {
    return 'Default market must be included in the selected Market Access.';
  }

  if (!allFacilities && facilityIds.length === 0) {
    return 'Select at least one facility or set Facility Access to All.';
  }

  if (!allBrands && brandIds.length === 0) {
    return 'Select at least one brand or set Brand Access to All.';
  }

  return null;
};

export const toUserAccessScopePayload = (values) => {
  const allMarkets = values?.allMarkets === true;
  const allFacilities = values?.allFacilities === true;
  const allBrands = values?.allBrands === true;

  const marketIds = normalizeScopeIds(values?.marketIds);
  const requestedDefaultMarketId = normalizeScopeIds(values?.defaultMarketId)[0] ?? null;
  const defaultMarketId = allMarkets || requestedDefaultMarketId === null
    || marketIds.includes(requestedDefaultMarketId)
    ? requestedDefaultMarketId
    : null;
  const facilityIds = normalizeScopeIds(values?.facilityIds);
  const brandIds = normalizeScopeIds(values?.brandIds);

  return {
    allMarkets,
    marketIds: allMarkets ? [] : marketIds,
    defaultMarketId,
    allFacilities,
    facilityIds: allFacilities ? [] : facilityIds,
    allBrands,
    brandIds: allBrands ? [] : brandIds,
  };
};

export const permissionSetToRole = (perms, ignoreLandingPages: boolean = true): string => {
  const incoming = perms && typeof perms === 'object' ? perms : {};
  const roleOrder = ['admin', 'manager', 'designer', 'viewer'];
  for (const role of roleOrder) {
    const expected = roleToPermissionSet(role);
    let matches = true;
    for (const key of Object.keys(expected)) {
      if (ignoreLandingPages && key === 'landingPages') continue;
      if (!Object.prototype.hasOwnProperty.call(incoming, key)) {
        matches = false;
        break;
      }
      if (Number(incoming[key]) !== Number(expected[key])) {
        matches = false;
        break;
      }
    }
    if (matches) return role;
  }
  return 'viewer';
};
